// ============================================================================
// src/lib/mentionEmailApi.ts — the browser's two touchpoints on mention email.
// ============================================================================
// Neither of these decides anything about delivery. Migration 209's trigger
// queues the email the moment a mention notification is written, and
// supabase/functions/mention-emails resolves the recipient server-side from
// the saved mention. The browser only:
//
//   1. nudgeMentionEmails — asks the drain to run NOW, so a mention arrives in
//      the recipient's inbox in seconds instead of waiting for the sweep. It
//      carries no recipient, no address and no comment text: the outbox is the
//      only thing that decides who gets mail. Losing this call costs latency,
//      never delivery.
//
//   2. loadNotificationTarget — resolves an emailed /?n=<id> link back to the
//      record it points at, through the same recipient-only gate the
//      notification bell already uses.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';

// The query parameter an emailed mention link carries. It hangs off the home
// path rather than a route of its own so that a signed-out click still lands
// somewhere real and the parameter survives the login round-trip.
export const MENTION_LINK_PARAM = 'n';

// Mention links are only ever minted against the home path, and this helper
// refuses to read the parameter anywhere else.
//
// That restriction is load-bearing, not tidiness. The pig-sows route fence
// (tests/static/pig_sows_fence_static.test.js) admits search and hash carriers
// on the stated premise that NOTHING in the SowsView import subtree reads them
// — and Header, which consumes this, is in that subtree. Confining the read to
// the home path makes that premise true by construction rather than by
// argument: on /pig/sows, or any other route, this returns null and the
// handler does nothing at all.
export const MENTION_LINK_PATHNAME = '/';

export function readMentionLinkId(loc?: {pathname: string; search: string}): string | null {
  const target = loc ?? (typeof window === 'undefined' ? null : window.location);
  if (!target || target.pathname !== MENTION_LINK_PATHNAME) return null;
  try {
    return new URLSearchParams(target.search).get(MENTION_LINK_PARAM) || null;
  } catch {
    return null;
  }
}

// A transport failure and "that notification is not yours" must be
// distinguishable. Collapsing both to null is what made the emailed link fail
// silently: a request that never reached the server looked exactly like a
// notification the caller may not see, so the handler gave up permanently on
// something that would have succeeded on a retry.
export type NotificationTargetResult = {ok: true; target: NotificationTarget | null} | {ok: false; error: string};

export type NotificationTarget = {
  id: string;
  type: string | null;
  task_instance_id: string | null;
  activity_entity_type: string | null;
  activity_entity_id: string | null;
  comment_id: string | null;
  read_at: string | null;
};

/**
 * Fire-and-forget request for a prompt drain of the mention email outbox.
 *
 * Deliberately NOT awaited by its callers and deliberately never throwing: a
 * comment that saved successfully must not report a failure because the mail
 * nudge could not be delivered. Anything this misses is picked up by the
 * scheduled sweep, and the outbox's unique (source, recipient) key means an
 * extra nudge can never produce an extra email.
 */
export function nudgeMentionEmails(sb: SupabaseClient, mentionCount: number): void {
  if (!sb || !mentionCount || mentionCount < 1) return;
  try {
    const request = sb.functions.invoke('mention-emails', {body: {mode: 'nudge'}});
    if (request && typeof request.catch === 'function') {
      request.catch(() => {
        /* Latency only. The sweep retries. */
      });
    }
  } catch {
    /* Same: never surface a delivery problem as a save problem. */
  }
}

/**
 * Resolve one notification for the signed-in recipient.
 *
 * `{ok: true, target: null}` means the lookup SUCCEEDED and the notification is
 * not this caller's — that is what makes a forwarded mention link inert, and it
 * is deliberately not retried. `{ok: false}` means the request itself failed and
 * is worth retrying. The caller must not confuse the two.
 */
export async function loadNotificationTarget(
  sb: SupabaseClient,
  notificationId: string,
): Promise<NotificationTargetResult> {
  if (!sb || !notificationId) return {ok: true, target: null};
  try {
    const {data, error} = await sb.rpc('get_notification_target', {
      p_notification_id: notificationId,
    });
    if (error) return {ok: false, error: error.message || 'lookup failed'};
    const rows = Array.isArray(data) ? data : [];
    return {ok: true, target: rows.length > 0 ? (rows[0] as NotificationTarget) : null};
  } catch (e) {
    // A rejected promise (offline, aborted, DNS) is a transport failure too.
    return {ok: false, error: e instanceof Error ? e.message : 'lookup failed'};
  }
}
